import { defineStore } from 'pinia'
import { v4 as uuid4 } from 'uuid'

import eventSender from '@last/core-ui/scripts/eventSender'
import { supportedLanguages } from '@last/core/src/supportedLanguages'

import api from '@/api.js'
import { useOnboarding } from '@/composables/useOnboarding'
import router from '@/router'
import pinia from '@/store/index'
import { useLocationsStore } from '@/store/locations'

const { getAnalyticsData } = useOnboarding()

window.addEventListener('storage', e => {
  if (e.key === 'accessToken' && !localStorage.getItem('accessToken')) {
    let auth = useAuthStore(pinia)
    auth.logout()
  }
})

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: localStorage.getItem('accessToken'),
    user: {
      id: localStorage.getItem('userId'),
      name: '',
      permissions: {}
    },
    onboarding: {
      completed: false,
      businessType: null,
      businessName: null,
      taxId: null,
      businessAddress: null,
      locationName: null,
      locationNumberRange: null,
      locationType: null,
      locationAddress: null,
      locationCity: null,
      locationLat: null,
      locationLng: null,
      locationCountryCode: null,
      locationOpensAt: null,
      bookingService: null
    }
  }),
  getters: {
    isAuthenticated: state => !!state.accessToken,
    rootUser: state => state.user,
    permissionsForOrganization: state => organizationId => {
      return (
        state.user.permissions[organizationId]?.permissions ||
        state.user.permissions['support']?.permissions ||
        []
      ).reduce((res, permission) => {
        res[permission] = true
        return res
      }, {})
    },
    permissions: state => {
      let locations = useLocationsStore()
      return (
        state.user.permissions['support']?.permissions ||
        state.user.permissions[
          locations.selectedLocation?.organizationId ||
            locations.selectedOrganizationId
        ]?.permissions ||
        []
      ).reduce((res, permission) => {
        res[permission] = true
        return res
      }, {})
    },
    defaultPrivileges: () => {
      return [
        'PRODUCT_MANAGER',
        'CASH_DRAWER',
        'REAL_TIME_REPORTER',
        'DELIVERY_MANAGER',
        'DISCOUNT_MANAGER',
        'TILL_MANAGER',
        'SHIFT_MANAGER',
        'OPEN_PRODUCTS_CREATOR'
      ]
    },
    isSupport: state => {
      return 'support' in state.user.permissions
    },
    showEulaModal: state => {
      const locations = useLocationsStore()
      const isSupport = 'support' in state.user.permissions
      const showLocationModal = locations.locationList.some(
        location => location.eulaAccepted === false
      )
      const showUserModal = state.user.eulaAccepted === false
      return (showLocationModal || showUserModal) && !isSupport
    },
    isOnboardingUser: state => {
      return state.onboarding && !state.onboarding.completed
    },
    hasUserLanguage: state => {
      return !!state.user?.language
    },
    getUserBrowserLanguage: () => {
      const browserLang = window.navigator.language.split('-')[0]
      return supportedLanguages
        .map(suppLang => suppLang.value)
        .includes(browserLang)
        ? browserLang
        : 'es'
    }
  },
  actions: {
    async handleTokenRefreshed(token) {
      let locations = useLocationsStore()
      locations.removeSelectedLocation()
      this.accessToken = token
      localStorage.setItem('accessToken', token)
      await this.handleUserAuth()
    },
    refreshUserId() {
      if (this.user.id) return this.user.id
      const userId = localStorage.getItem('userId') ?? uuid4()
      this.user.id = userId
      localStorage.setItem('userId', userId)
      return userId
    },
    async signUp(signUpData) {
      const {
        email,
        password,
        name,
        phone,
        marketingCommunication,
        analyticsData
      } = signUpData
      const response = await api.post('/dashboard/users/signup', {
        id: this.user.id,
        email,
        password,
        name,
        phone,
        analyticsData,
        marketingCommunication
      })
      await this.handleTokenRefreshed(response.data.token)
      eventSender.sendNewLead()
    },
    async login(loginData) {
      let response = await api.post('/dashboard/users/login', loginData)
      await this.handleTokenRefreshed(response.data.token)
    },
    async updateUserLanguage(language) {
      await api.put('/users/language', { language })
    },
    async updateUser({ language, ...update }) {
      await api.put('/dashboard/users/profile', {
        id: this.user.id,
        ...update
      })
      if (language) await this.updateUserLanguage(language)
      await this.refreshCurrentUser()
    },
    async redirectLogin({ accessToken }) {
      if (accessToken) {
        this.accessToken = accessToken
        localStorage.setItem('accessToken', accessToken)
        return true
      }
      return false
    },
    async supportLogin({
      accessToken,
      selectedOrganizationId,
      selectedLocationId
    }) {
      let response = await api.get(`/users/token/${accessToken}/checkSupport`)
      let supportUserIsValid = response.data
      if (supportUserIsValid) {
        this.accessToken = accessToken
        localStorage.setItem('accessToken', accessToken)
        if (selectedOrganizationId) {
          let locations = useLocationsStore()
          locations.setSelectedOrganizationId(selectedOrganizationId)
          if (selectedLocationId) {
            locations.setSelectedLocationId(selectedLocationId)
          }
        }
        return true
      }
      return false
    },
    async logout() {
      await api.post('/users/logout')
      this.clearAndGoToLogin()
    },
    clearAndGoToLogin() {
      const locations = useLocationsStore()
      locations.clear()
      this.clear()
      localStorage.clear()
      router.push({ name: 'login' })
    },
    clear() {
      this.accessToken = null
      this.user = {
        permissions: {}
      }
    },
    async refreshCurrentUser() {
      let response = await api.get('/dashboard/users/me')
      let user = response.data
      try {
        $zoho.salesiq.visitor.id(user.id)
        $zoho.salesiq.visitor.email(user.email)
        $zoho.salesiq.visitor.name(user.name)
        window.pagesense = window.pagesense || []
        window.pagesense.push([
          'trackUser',
          { id: user.id, name: user.name, email: user.email }
        ])
        window.pagesense.push(['identifyUser', `${user.name} <${user.email}>`])
      } catch {
        // Don't crash if zoho crashes
      }
      this.user = user
      localStorage.setItem('userId', user.id)
    },
    async acceptEula() {
      const locations = useLocationsStore().locationList
      const acceptedLocations = this.isSupport
        ? []
        : locations
            .filter(location => !location.eulaAccepted)
            .map(location => location.id)
      await api.post('/dashboard/eula-accepted', {
        userEulaAccepted: !this.user.eulaAccepted,
        locationsEulaAccepted: acceptedLocations
      })
      this.user.eulaAccepted = true
      locations.forEach(location => {
        location.eulaAccepted = true
      })
    },
    async handleUserAuth() {
      if (!this.accessToken) return
      await this.refreshCurrentUser()
      if (!this.hasUserLanguage)
        await this.updateUserLanguage(this.getUserBrowserLanguage)
      const locations = useLocationsStore()
      await locations.refreshLocations()
      await this.refreshOnboarding()
      if (this.isOnboardingUser) return router.push({ name: 'onboarding' })
      if (locations.selectedOrganizationId) {
        const redirect = router.currentRoute.value.query?.redirect
        return router.push({ name: redirect || 'home' })
      } else {
        return router.push({ name: 'createOrganization' })
      }
    },
    async refreshOnboarding() {
      await this.refreshCurrentUser()
      if (!this.user.id) return

      const locations = useLocationsStore()
      if (locations.organizations[locations.selectedOrganizationId]?.isDemo) {
        this.onboarding.completed = true
        return
      }
      try {
        let response = await api.get(`/dashboard/onboarding/user`)
        this.onboarding = response.data
      } catch {
        this.onboarding.completed = true
      }
    },
    async updateOnboarding(payload, options = { silent: false }) {
      if (!options.silent)
        await api.put(`/dashboard/onboarding/user`, {
          update: payload,
          analyticsData: getAnalyticsData()
        })
      this.onboarding = {
        ...this.onboarding,
        ...payload
      }
    },
    async createNewOnboarding() {
      let response = await api.post('/dashboard/onboarding/user')
      this.onboarding = response.data
    }
  }
})
